/**
 * Skat app entry — a paint-first boot shim (paint-first-chunked-boot design). Kicks off
 * bootPaintFirst() and returns immediately: no top-level await, so DOMContentLoaded/load fire at
 * parse-end instead of after graph eval (the module runs after parsing, so the DOM is already
 * complete when this executes — there is no DOMContentLoaded listener to wire).
 *
 * bootPaintFirst() awaits the first committed frame (afterNextPaint), then walks the eager graph
 * in dependency order, yielding on a frame budget so the walk never blocks a task past
 * Lighthouse's long-task threshold, then makes the authoritative tail imports (boot.js,
 * identity-cookies.js) that absorb this module's old body (cookie sync + init(app) call).
 *
 * For card design iteration, use /card-lab instead.
 */
import { EAGER_MODULES } from "./generated/eager-modules.js";

// why: Lighthouse's 50ms long-task threshold at its 4x simulated mobile CPU
// is 12.5ms real. The check runs BEFORE an import, so a slice's true size is
// budget + one module's eval (~2-3ms typical at 79 modules / ~161ms) — 8ms
// keeps that sum under 12.5ms for typical modules; a single heavy module
// (vendor/gsap.js) can exceed it regardless of budget, the accepted residual.
const YIELD_BUDGET_MS = 8;

// Resolves after the next frame commits: the rAF callback runs during the
// rendering step, so a task scheduled from inside it can only fire after the
// frame — with the splash — has painted. Raced against the page going hidden,
// where rAF stops firing and there is no paint left to protect.
function afterNextPaint() {
  return new Promise((resolve) => {
    const onHidden = () => {
      if (document.hidden) resolve(); // resolve() is idempotent — no double-resolve guard needed
    };
    document.addEventListener("visibilitychange", onHidden);
    requestAnimationFrame(() =>
      setTimeout(() => {
        document.removeEventListener("visibilitychange", onHidden);
        resolve();
      }),
    );
  });
}

const yieldTask = () =>
  "scheduler" in window && scheduler.yield != null
    ? scheduler.yield()
    : new Promise((resolve) => setTimeout(resolve));

async function bootPaintFirst() {
  if (!document.hidden) await afterNextPaint();
  let sliceStart = performance.now();
  for (const url of EAGER_MODULES) {
    if (performance.now() - sliceStart > YIELD_BUDGET_MS) {
      await yieldTask();
      sliceStart = performance.now();
    }
    try {
      await import(url);
    } catch (e) {
      // best-effort chunking only — the authoritative imports below decide
      // whether the graph is actually reachable
      console.warn(
        "[skat] eager chunk failed, deferring to boot import:",
        url,
        e.message,
      );
    }
  }
  const [{ init }, { syncIdentityCookies }] = await Promise.all([
    import("./boot.js"),
    import("./identity-cookies.js"),
  ]);
  syncIdentityCookies();
  const app = document.getElementById("skat-app");
  if (app != null) await init(app);
}

void bootPaintFirst().catch((e) => {
  // A failure before init()'s own try/finally (paint gate, authoritative
  // imports) would otherwise strand the splash forever. Degraded-but-alive,
  // same call app-loader.js makes for a failed skat.css: reveal what we have.
  console.error("[skat] boot failed before init — removing splash:", e);
  document.getElementById("app-loader")?.remove();
});
